import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import GridViewIcon from "@mui/icons-material/GridView";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import "./MapNavbar.css";
import "../mapToolbar/MapToolbar.css";
import { assets } from "../../../publics/assets";
import { ITEMS } from "../../../conts/navigationConstants";
import { STYLES } from "../../../conts/constants";

const thumb = (styleUrl) => {
  const path = styleUrl.replace("mapbox://styles/", "");
  return `https://api.mapbox.com/styles/v1/${path}/static/8.5,47,4/160x110?logo=false&attribution=false&access_token=${process.env.REACT_APP_MAPBOX_PK}`;
};

// Path to the scientific reference document in the public folder
const SCIENTIFIC_DOC_URL = `${process.env.PUBLIC_URL || ""}/reference-scientifique.pdf`;
const SCIENTIFIC_DOC_FILENAME = "reference-scientifique.pdf";

const MapNavbar = ({
  activeItem,
  onSelectItem,
  onProfile,
  logout,
  selectedStyle,
  onStyleChange,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showStyles, setShowStyles] = useState(false); // desktop popup
  const [drawerStyles, setDrawerStyles] = useState(false); // mobile drawer list
  const [showInfo, setShowInfo] = useState(false); // info popup (desktop)
  const [drawerInfo, setDrawerInfo] = useState(false); // info panel (mobile)
  const stylesRef = useRef(null);
  const infoRef = useRef(null);
  const [loggingOut, setLoggingOut] = useState(false);
 const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout?.();
    } finally {
      setLoggingOut(false);
    }
  };
  const closeDrawer = () => setDrawerOpen(false);

  const handleMobileSelect = (id) => {
    onSelectItem(id);
    closeDrawer();
  };

  // close the desktop popup when clicking outside (does not affect the drawer)
  useEffect(() => {
    const onClickOutside = (e) => {
      if (stylesRef.current && !stylesRef.current.contains(e.target))
        setShowStyles(false);
      if (infoRef.current && !infoRef.current.contains(e.target))
        setShowInfo(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Trigger the download of the scientific reference document
  const handleDownloadDoc = () => {
    const link = document.createElement("a");
    link.href = SCIENTIFIC_DOC_URL;
    link.download = SCIENTIFIC_DOC_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderStyles = (className, onPick) => (
    <div className={className}>
      {STYLES.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`map-toolbar__style ${selectedStyle === s.url ? "map-toolbar__style--active" : ""}`}
          onClick={() => {
            onStyleChange?.(s.url);
            onPick();
          }}
        >
          <img src={thumb(s.url)} alt="" />
          <span>{s.label}</span>
        </button>
      ))}
    </div>
  );

  // Reusable info content (used both in desktop popup & mobile drawer)
  const renderInfoContent = (onClose) => (
    <div className="map-info__content">
      <div className="map-info__head">
        <InfoOutlinedIcon fontSize="small" />
        <h3 className="map-info__title">Document de référence scientifique</h3>
        {onClose && (
          <IconButton
            className="map-info__close"
            onClick={onClose}
            aria-label="Fermer"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <p className="map-info__text">
        Consultez le document de référence scientifique qui détaille les
        méthodologies utilisées dans cette application.
      </p>
      <a
        className="map-info__download"
        href={SCIENTIFIC_DOC_URL}
        download={SCIENTIFIC_DOC_FILENAME}
        onClick={(e) => {
          // Fallback for browsers that don't honour the download attribute
          // on cross-origin or blob URLs — use JS download instead.
          if (SCIENTIFIC_DOC_URL.startsWith("blob:")) {
            e.preventDefault();
            handleDownloadDoc();
          }
        }}
      >
        <DownloadIcon fontSize="small" />
        <span>Télécharger le document</span>
      </a>
    </div>
  );

  return (
    <header className="map-navbar">
      <IconButton
        className="map-navbar__burger"
        onClick={() => setDrawerOpen(true)}
        aria-label="Ouvrir le menu"
      >
        <MenuIcon />
      </IconButton>

      <img
        className="map-navbar__logo"
        src={`${assets.edhec_climateInnov}`}
        alt="Logo"
      />

      {/* Desktop menu */}
      <nav className="map-navbar__menu">
        {ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`map-navbar__item ${activeItem === item.id ? "map-navbar__item--active" : ""}`}
            onClick={() => onSelectItem(item.id)}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}

        {/* Map styles: popup opens right under the button */}
        <div className="map-navbar__styles-wrap" ref={stylesRef}>
          <button
            type="button"
            className={`map-navbar__item ${showStyles ? "map-navbar__item--active" : ""}`}
            onClick={() => setShowStyles((v) => !v)}
            aria-expanded={showStyles}
          >
            <GridViewIcon fontSize="small" />
            <span>Styles de carte</span>
          </button>
          {showStyles &&
            renderStyles("map-toolbar__styles map-navbar__styles-pop", () =>
              setShowStyles(false),
            )}
        </div>
      </nav>

      {/* Desktop user actions */}
      <div className="map-navbar__user">
        {/* NEW: info icon + popup */}
        <div className="map-navbar__info-wrap" ref={infoRef}>
          <IconButton
            className={`map-navbar__info ${showInfo ? "map-navbar__info--active" : ""}`}
            onClick={() => setShowInfo((v) => !v)}
            aria-label="Informations"
            aria-expanded={showInfo}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          {showInfo && (
            <div className="map-info__pop" role="dialog">
              {renderInfoContent(() => setShowInfo(false))}
            </div>
          )}
        </div>
 
        {/* <IconButton
          className="map-navbar__avatar"
          onClick={onProfile}
          aria-label="Profil"
        >
          <PersonIcon fontSize="small" />
        </IconButton> */}
        <IconButton
          className="map-navbar__logout"
          onClick={handleLogout}
          disabled={loggingOut}
          aria-label="Déconnexion"
          title="Déconnexion"
        >
          <LogoutIcon fontSize="small" />
        </IconButton> 
      </div>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={closeDrawer}
        classes={{ paper: "map-drawer__paper" }}
      >
        <div className="map-drawer">
          <div className="map-drawer__head">
            <img
              className="map-drawer__logo"
              src={`${assets.edhec_climateInnov}`}
              alt="Logo"
            />
            <IconButton
              className="map-navbar__logout"
              onClick={closeDrawer}
              aria-label="Fermer le menu"
            >
              <CloseIcon />
            </IconButton>
          </div>

          <nav className="map-drawer__menu">
            {ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`map-drawer__item ${activeItem === item.id ? "map-drawer__item--active" : ""}`}
                onClick={() => handleMobileSelect(item.id)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}

            <button
              type="button"
              className={`map-drawer__item ${drawerStyles ? "map-drawer__item--active" : ""}`}
              onClick={() => setDrawerStyles((v) => !v)}
              aria-expanded={drawerStyles}
            >
              <GridViewIcon fontSize="small" />
              <span>Styles de carte</span>
            </button>
            {drawerStyles &&
              renderStyles("map-drawer__styles", () => {
                setDrawerStyles(false);
                closeDrawer();
              })}

            {/* NEW: info panel inside the mobile drawer */}
            <button
              type="button"
              className={`map-drawer__item ${drawerInfo ? "map-drawer__item--active" : ""}`}
              onClick={() => setDrawerInfo((v) => !v)}
              aria-expanded={drawerInfo}
            >
              <InfoOutlinedIcon fontSize="small" />
              <span>Informations</span>
            </button>
            {drawerInfo && (
              <div className="map-drawer__info">
                {renderInfoContent(() => setDrawerInfo(false))}
              </div>
            )}
          </nav>

          <div className="map-drawer__footer">
            {/* <button
              type="button"
              className="map-drawer__item"
              onClick={() => {
                onProfile?.();
                closeDrawer();
              }}
            >
              <PersonIcon fontSize="small" />
              <span>Profil</span>
            </button> */}
                        <button
              type="button"
              className="map-drawer__item"
              disabled={loggingOut}
              onClick={() => {
                closeDrawer();
                handleLogout();
              }}
            >
              <LogoutIcon fontSize="small" />
              <span>{loggingOut ? "Déconnexion…" : "Déconnexion"}</span>
            </button>
          </div>
        </div>
      </Drawer>
    </header>
  );
};

export default MapNavbar;